import React, { useEffect, useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';
import { Link } from '~/lib/react-router-dom';
import dayjs from '~/timeInit';

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

import GridPagination from '~/components/Common/Elements/GridPagination';

import { Voucher } from '~/services/PromoService';

type Props = {
  voucherName: string;
  setVoucherName: (v: string) => void;
  userEmail: string;
  setUserEmail: (v: string) => void;
  isActive: 'all' | 'active' | 'inactive';
  setIsActive: (v: 'all' | 'active' | 'inactive') => void;
  data: Array<Voucher>;
  isLoading: boolean;
  totalCount: number;
  pageNum: number;
  pageSize: number;
  onPageChange: (page: number, pageSize: number) => void;
  onDelete: (id: string) => Promise<void> | void;
};

const sharedOpts: Partial<GridColDef> = {
  editable: false,
  sortable: false,
  filterable: false,
  hideable: false,
  disableColumnMenu: true,
  flex: 0.5,
};

function getColumns({ onDelete }: { onDelete: (row: Voucher) => void }) {
  const columns: Array<GridColDef> = [
    {
      field: 'voucher_id',
      headerName: 'ID',
      ...sharedOpts,
      renderCell: (params) => params.row.voucher_id || '',
    },
    {
      field: 'voucher_name',
      headerName: 'Voucher Name',
      ...sharedOpts,
      renderCell: (params) => params.row.voucher_name || '',
    },
    {
      field: 'user_id',
      headerName: 'User ID',
      ...sharedOpts,
      renderCell: (params) => {
        const userId = params.row.user_id;
        if (!userId) {
          return '';
        }
        return <Link to={`/users/${userId}`}>{userId}</Link>;
      },
    },
    {
      field: 'created_at',
      headerName: 'Created At',
      ...sharedOpts,
    },
    {
      field: 'updated_at',
      headerName: 'Updated At',
      ...sharedOpts,
    },
    {
      field: 'used_at',
      headerName: 'Used At',
      ...sharedOpts,
    },
    {
      field: 'expired_at',
      headerName: 'Expired At',
      ...sharedOpts,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      ...sharedOpts,
      renderCell: (params) => {
        const isExpired = Boolean(params.row.expired_at && dayjs(params.row.expired_at).isBefore(dayjs()));
        return (
          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              variant="outlined"
              size="small"
              component={Link}
              to={`/marketing/vouchers/${params.row.voucher_id}`}
              disabled={isExpired}
            >
              Edit
            </Button>
            <Button variant="outlined" size="small" onClick={() => onDelete(params.row)} disabled={isExpired}>
              Delete
            </Button>
          </Stack>
        );
      },
    },
  ];
  return columns;
}

export default function VouchersTab({
  voucherName,
  setVoucherName,
  userEmail,
  setUserEmail,
  isActive,
  setIsActive,
  data,
  isLoading,
  totalCount,
  pageNum,
  pageSize,
  onPageChange,
  onDelete,
}: Props) {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [rowToDelete, setRowToDelete] = useState<Voucher | undefined>(undefined);
  const { enqueueSnackbar } = useSnackbar();
  const [userEmailInput, setUserEmailInput] = useState(userEmail);

  const rows = useMemo(() => data.map((row) => ({ id: row.voucher_id, ...row })), [data]);

  useEffect(() => {
    setUserEmailInput(userEmail);
  }, [userEmail]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (userEmailInput !== userEmail) {
        setUserEmail(userEmailInput);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [userEmailInput, userEmail, setUserEmail]);

  const handleDelete = (row: Voucher) => {
    setRowToDelete(row);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (rowToDelete) {
      try {
        await onDelete(rowToDelete.voucher_id);
        setDeleteDialogOpen(false);
        setRowToDelete(undefined);
      } catch (error) {
        enqueueSnackbar('Failed to delete voucher', { variant: 'error' });
      }
    }
  };

  const cancelDelete = () => {
    setDeleteDialogOpen(false);
    setRowToDelete(undefined);
  };

  return (
    <>
      <Grid container spacing={2} justifyContent="flex-end" alignItems="end">
        <Grid size={12} container spacing={2}>
          <Grid size={4}>
            <TextField
              label="Voucher Name"
              value={voucherName}
              onChange={(e) => setVoucherName(e.target.value)}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid size={4}>
            <TextField
              label="User Email"
              value={userEmailInput}
              onChange={(e) => setUserEmailInput(e.target.value)}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid size={4}>
            <FormControl fullWidth size="small">
              <InputLabel id="voucher-active-label">Status</InputLabel>
              <Select
                labelId="voucher-active-label"
                value={isActive}
                label="Status"
                onChange={(e) => setIsActive(e.target.value as 'all' | 'active' | 'inactive')}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
        <Grid size={4} container spacing={2}>
          <Stack direction="row" spacing={2} justifyContent="flex-end" width="100%">
            <Button variant="contained" color="primary" component={Link} to="/marketing/vouchers-create">
              New Voucher
            </Button>
          </Stack>
        </Grid>
      </Grid>

      <Dialog open={deleteDialogOpen} onClose={cancelDelete}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>Are you sure you want to delete this voucher?</DialogContent>
        <DialogActions>
          <Button onClick={cancelDelete} variant="text">
            Cancel
          </Button>
          <Button onClick={confirmDelete} variant="contained" color="primary">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Box mt={2}>
        <DataGrid
          slots={{ pagination: GridPagination }}
          onPaginationModelChange={({ page, pageSize }) => onPageChange(page, pageSize)}
          loading={isLoading}
          rows={rows}
          columns={getColumns({ onDelete: handleDelete })}
          autoHeight
          rowCount={totalCount}
          paginationMode="server"
          pageSizeOptions={[20, 50, 100]}
          paginationModel={{ page: pageNum, pageSize: pageSize }}
        />
      </Box>
    </>
  );
}
